/**
 * Yönetici yetkileri — granular permission sistemi.
 *
 * Mimari:
 *  - BOSS her yetkiye sahip (bypass)
 *  - Personel rol'ünde olanlar `yoneticiMi: true` ise yönetici paneline girer
 *  - Hangi modüllere/aksiyonlara erişebileceği `yoneticiYetkileri: YetkiId[]` ile belirlenir
 *  - Yetkiler personelin atandığı şubelerle sınırlıdır (subeIdleri kapsam)
 *
 * Yetki ID formatı: "modul.aksiyon" — örn. "puantaj.ekle"
 */

export const YETKILER = {
  // Personel yönetimi
  PERSONEL_LISTELE: 'personel.listele',
  PERSONEL_EKLE: 'personel.ekle',
  PERSONEL_DUZENLE: 'personel.duzenle',
  PERSONEL_PASIFLESTIR: 'personel.pasiflestir',
  PERSONEL_BELGE_YUKLE: 'personel.belge_yukle',
  PERSONEL_BELGE_INDIR: 'personel.belge_indir',
  PERSONEL_BELGE_SIL: 'personel.belge_sil',
  PERSONEL_PROFIL_FOTO_DEGISTIR: 'personel.profil_foto_degistir',
  PERSONEL_QR_OLUSTUR: 'personel.qr_olustur',

  // Vardiya
  VARDIYA_PLANLA: 'vardiya.planla',
  VARDIYA_KALIP_YONET: 'vardiya.kalip_yonet',
  VARDIYA_GOR: 'vardiya.gor',

  // Giriş/Çıkış kayıt
  KAYIT_MANUEL_TALEP: 'kayit.manuel_talep',
  KAYIT_ONAY: 'kayit.onay',
  KAYIT_GECMIS_GOR: 'kayit.gecmis_gor',
  KAYIT_SIL: 'kayit.sil',

  // Mola
  MOLA_ONAYLA: 'mola.onayla',
  MOLA_GECMIS_GOR: 'mola.gecmis_gor',

  // Avans
  AVANS_LISTE_GOR: 'avans.liste_gor',
  AVANS_ONAYLA: 'avans.onayla',
  AVANS_ODE_ISARETLE: 'avans.ode_isaretle',

  // İzin
  IZIN_LISTE_GOR: 'izin.liste_gor',
  IZIN_ONAYLA: 'izin.onayla',
  IZIN_BAKIYE_GOR: 'izin.bakiye_gor',

  // Puantaj (yetki tek bir flag: yapabilir mi)
  PUANTAJ_EKLE: 'puantaj.ekle',
  PUANTAJ_TUM_GOR: 'puantaj.tum_gor',

  // Maaş
  MAAS_GORUNTULE: 'maas.goruntule',
  MAAS_DUZENLE: 'maas.duzenle',
  MAAS_HESAPLAMA_CALISTIR: 'maas.hesaplama_calistir',
  MAAS_CSV_EXPORT: 'maas.csv_export',
  MAAS_HAREKET_EKLE: 'maas.hareket_ekle',
  MAAS_AY_KAPANIS: 'maas.ay_kapanis',

  // Görev
  GOREV_ATA: 'gorev.ata',
  GOREV_KAPATMA_ONAY: 'gorev.kapatma_onay',
  GOREV_GOR: 'gorev.gor',

  // Rapor
  RAPOR_MALIYET: 'rapor.maliyet',
  RAPOR_SISTEM_LOG: 'rapor.sistem_log',
  RAPOR_CSV_EXPORT: 'rapor.csv_export',
} as const

export type YetkiId = typeof YETKILER[keyof typeof YETKILER]

/** Modüllerin gruplaması (UI'da bölümlere ayırmak için) */
export interface YetkiGrubu {
  baslik: string
  yetkiler: { id: YetkiId; label: string; aciklama?: string }[]
}

export const YETKI_GRUPLARI: YetkiGrubu[] = [
  {
    baslik: 'Personel',
    yetkiler: [
      { id: YETKILER.PERSONEL_LISTELE, label: 'Personel listele' },
      { id: YETKILER.PERSONEL_EKLE, label: 'Yeni personel ekle' },
      { id: YETKILER.PERSONEL_DUZENLE, label: 'Personel düzenle' },
      { id: YETKILER.PERSONEL_PASIFLESTIR, label: 'Personel pasifleştir' },
      { id: YETKILER.PERSONEL_BELGE_YUKLE, label: 'Belge yükle' },
      { id: YETKILER.PERSONEL_BELGE_INDIR, label: 'Belge indir' },
      { id: YETKILER.PERSONEL_BELGE_SIL, label: 'Belge sil' },
      { id: YETKILER.PERSONEL_PROFIL_FOTO_DEGISTIR, label: 'Profil fotoğrafı değiştir' },
      { id: YETKILER.PERSONEL_QR_OLUSTUR, label: 'Setup QR oluştur (mobil cihaz bağla)' },
    ],
  },
  {
    baslik: 'Vardiya',
    yetkiler: [
      { id: YETKILER.VARDIYA_GOR, label: 'Vardiyaları gör' },
      { id: YETKILER.VARDIYA_PLANLA, label: 'Vardiya planla' },
      { id: YETKILER.VARDIYA_KALIP_YONET, label: 'Kalıp yönet' },
    ],
  },
  {
    baslik: 'Giriş/Çıkış Kayıt',
    yetkiler: [
      { id: YETKILER.KAYIT_GECMIS_GOR, label: 'Kayıt geçmişini gör' },
      { id: YETKILER.KAYIT_MANUEL_TALEP, label: 'Manuel kayıt talep et (selfie zorunlu)' },
      { id: YETKILER.KAYIT_ONAY, label: 'Manuel kayıt onay/red' },
      { id: YETKILER.KAYIT_SIL, label: 'Kayıt sil/düzelt' },
    ],
  },
  {
    baslik: 'Mola',
    yetkiler: [
      { id: YETKILER.MOLA_GECMIS_GOR, label: 'Mola geçmişini gör' },
      { id: YETKILER.MOLA_ONAYLA, label: 'Mola onayla/reddet' },
    ],
  },
  {
    baslik: 'Avans',
    yetkiler: [
      { id: YETKILER.AVANS_LISTE_GOR, label: 'Avans listesini gör' },
      { id: YETKILER.AVANS_ONAYLA, label: 'Avans onayla/reddet (değiştirilebilir)' },
      { id: YETKILER.AVANS_ODE_ISARETLE, label: 'Avans ödendi olarak işaretle' },
    ],
  },
  {
    baslik: 'İzin',
    yetkiler: [
      { id: YETKILER.IZIN_LISTE_GOR, label: 'İzin taleplerini gör' },
      { id: YETKILER.IZIN_ONAYLA, label: 'İzin onayla/reddet' },
      { id: YETKILER.IZIN_BAKIYE_GOR, label: 'İzin bakiyelerini gör' },
    ],
  },
  {
    baslik: 'Puantaj',
    yetkiler: [
      { id: YETKILER.PUANTAJ_EKLE, label: 'Puantaj ekle (tutar Ayarlar\'dan, 1 saat içinde kendi kaydını iptal edebilir)' },
      { id: YETKILER.PUANTAJ_TUM_GOR, label: 'Tüm puantajları ve toplam kesintiyi gör' },
    ],
  },
  {
    baslik: 'Maaş',
    yetkiler: [
      { id: YETKILER.MAAS_GORUNTULE, label: 'Maaşları görüntüle' },
      { id: YETKILER.MAAS_DUZENLE, label: 'Maaş alanları düzenle' },
      { id: YETKILER.MAAS_HESAPLAMA_CALISTIR, label: 'Maaş hesaplaması çalıştır' },
      { id: YETKILER.MAAS_HAREKET_EKLE, label: 'Manuel maaş hareketi ekle (kasa eksiği vb.)' },
      { id: YETKILER.MAAS_AY_KAPANIS, label: 'Ay kapanışı yap' },
      { id: YETKILER.MAAS_CSV_EXPORT, label: 'CSV export' },
    ],
  },
  {
    baslik: 'Görev',
    yetkiler: [
      { id: YETKILER.GOREV_GOR, label: 'Görevleri gör' },
      { id: YETKILER.GOREV_ATA, label: 'Görev ata' },
      { id: YETKILER.GOREV_KAPATMA_ONAY, label: 'Görev kapatma onayı' },
    ],
  },
  {
    baslik: 'Rapor',
    yetkiler: [
      { id: YETKILER.RAPOR_MALIYET, label: 'Maliyet sayfası' },
      { id: YETKILER.RAPOR_SISTEM_LOG, label: 'Sistem logları' },
      { id: YETKILER.RAPOR_CSV_EXPORT, label: 'Rapor CSV export' },
    ],
  },
]

/** Tüm yetkilerin düz listesi (preset oluşturmak için) */
export const TUM_YETKILER: YetkiId[] = YETKI_GRUPLARI.flatMap(g => g.yetkiler.map(y => y.id))

/**
 * Preset setler — BOSS hızlı atama için kullanabilir.
 * "Mağaza Sorumlusu" tipik bir tam ofset, "Muhasebe" daha kısıtlı.
 */
export const YETKI_PRESETLERI = {
  // Mevcut sayfalar: Personel + Vardiyalar + Molalar + Puantaj + Görevler.
  // Diğer yetkiler (avans, izin, kayıt geçmişi vb.) sayfa açılınca eklenir.
  magazaSorumlusu: [
    YETKILER.PERSONEL_LISTELE,
    YETKILER.PERSONEL_BELGE_INDIR,
    YETKILER.VARDIYA_GOR,
    YETKILER.VARDIYA_PLANLA,
    YETKILER.MOLA_GECMIS_GOR,
    YETKILER.PUANTAJ_EKLE,
    YETKILER.GOREV_GOR,
    YETKILER.GOREV_ATA,
    YETKILER.GOREV_KAPATMA_ONAY,
  ] as YetkiId[],
  muhasebe: [
    YETKILER.PERSONEL_LISTELE,
    YETKILER.PERSONEL_BELGE_YUKLE,
    YETKILER.PERSONEL_BELGE_INDIR,
    YETKILER.PERSONEL_BELGE_SIL,
    YETKILER.AVANS_LISTE_GOR,
    YETKILER.AVANS_ODE_ISARETLE,
    YETKILER.IZIN_LISTE_GOR,
    YETKILER.IZIN_BAKIYE_GOR,
    YETKILER.PUANTAJ_TUM_GOR,
    YETKILER.MAAS_GORUNTULE,
    YETKILER.MAAS_HAREKET_EKLE,
    YETKILER.MAAS_CSV_EXPORT,
    YETKILER.RAPOR_MALIYET,
    YETKILER.RAPOR_CSV_EXPORT,
  ] as YetkiId[],
}
